import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const configElement = document.getElementById('thread-protection-config');

if (configElement) {
  const config = JSON.parse(configElement.textContent);
  let pending = Promise.resolve();
  let busy = 0;
  let failed = false;
  const root = config.root;
  const isProtection = (item) => Number(item.product_id) === Number(config.productId);
  const feeFor = (items) => {
    const subtotal = items.filter((item) => !isProtection(item))
      .reduce((sum, item) => sum + item.original_line_price, 0);
    return subtotal > 0 && subtotal <= 500000 ? Math.max(3, Math.ceil(subtotal / 10000)) * 100 : 0;
  };
  const controls = () => document.querySelectorAll('[data-thread-protection-toggle]');
  const setBusy = (value) => {
    for (const checkbox of controls()) checkbox.disabled = value;
    for (const button of document.querySelectorAll('[name="checkout"], .additional-checkout-buttons')) {
      if (value || failed) {
        if (!button.hasAttribute('data-thread-was-disabled')) button.dataset.threadWasDisabled = String(Boolean(button.disabled));
        if ('disabled' in button) button.disabled = true;
        button.inert = true;
      } else if (button.hasAttribute('data-thread-was-disabled')) {
        if ('disabled' in button) button.disabled = button.dataset.threadWasDisabled === 'true';
        button.inert = false;
        delete button.dataset.threadWasDisabled;
      }
    }
  };
  const request = async (path, body) => {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const cart = await response.json();
    if (!response.ok || cart.errors) throw new Error(config.error);
    return cart;
  };
  const reconcile = async (selection = null) => {
    const cart = await request('cart.js');
    const protectedLines = cart.items.filter(isProtection);
    const selected = selection ?? protectedLines.length > 0;
    const price = feeFor(cart.items);
    const variant = config.variants.find((item) => item.price === price);
    const target = selected && variant ? Number(variant.id) : null;
    for (const checkbox of controls()) checkbox.checked = Boolean(target);
    if (protectedLines.length === 0 && !target) return;
    if (protectedLines.length === 1 && Number(protectedLines[0].variant_id) === target && protectedLines[0].quantity === 1) return;
    const updates = {};
    let matched = false;
    for (const item of protectedLines) {
      const keep = !matched && Number(item.variant_id) === target;
      updates[item.key] = keep ? 1 : 0;
      matched ||= keep;
    }
    if (target && !matched) updates[target] = 1;
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId))].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart',
      lines: protectedLines.length ? protectedLines.map((item) => ({ id: item.key, quantity: 0 })) : [{ id: String(target), quantity: 1 }],
      promise: deferred.promise, detail: { source: 'thread-protection' },
    }));
    try {
      const updated = await request('cart/update.js', {
        updates, sections: sectionIds, sections_url: location.pathname,
      });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: { sections: updated.sections, items: updated.items, itemCount: updated.item_count, source: 'thread-protection', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  };
  const enqueue = (selection = null) => {
    busy += 1;
    setBusy(true);
    pending = pending.then(async () => {
      await reconcile(selection);
      failed = false;
      for (const element of document.querySelectorAll('[data-thread-protection-error]')) element.hidden = true;
    }).catch(async () => {
      failed = true;
      for (const element of document.querySelectorAll('[data-thread-protection-error]')) {
        element.textContent = config.error;
        element.hidden = false;
      }
      try {
        const cart = await request('cart.js');
        for (const checkbox of controls()) checkbox.checked = cart.items.some(isProtection);
      } catch {}
    }).finally(() => {
      busy -= 1;
      if (!busy) setBusy(false);
    });
    return pending;
  };
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-thread-protection-toggle]')) return;
    enqueue(event.target.checked);
  });
  for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
    document.addEventListener(name, (event) => {
      if (event.detail?.source === 'thread-protection') return;
      busy += 1;
      setBusy(true);
      Promise.resolve(event.promise).then(({ detail } = {}) => {
        if (detail?.source !== 'thread-protection') return enqueue();
      }).catch(() => {}).finally(() => {
        busy -= 1;
        if (!busy) setBusy(false);
      });
    });
  }
  document.addEventListener('submit', (event) => {
    if ((busy || failed) && event.submitter?.name === 'checkout') event.preventDefault();
  }, true);
  const observer = new MutationObserver(() => { if (busy) setBusy(true); });
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => enqueue());
  enqueue();
}
